import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { isSystemAdmin } from './aivisRoles';
import './ai-vis.css';

function formatAbsolute(isoString) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function ClientLogo({ logoUrl, domain, name, size = 26 }) {
  const [failed, setFailed] = useState(false);
  const src = (!failed && logoUrl) ? logoUrl : null;
  const fallbackLetter = (name || '?').slice(0, 1).toUpperCase();
  if (src) {
    return (
      <img
        src={src} alt="" width={size} height={size}
        style={{ borderRadius: '50%', objectFit: 'contain', flexShrink: 0, border: '1px solid var(--aivis-border)' }}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%', background: 'var(--aivis-bg)',
      border: '1px solid var(--aivis-border)', display: 'inline-flex', alignItems: 'center',
      justifyContent: 'center', fontSize: size * 0.42, fontWeight: 700,
      color: 'var(--aivis-text-muted)', flexShrink: 0,
    }}>
      {fallbackLetter}
    </span>
  );
}

function OrgLogo({ logoUrl, name, size = 22 }) {
  const [failed, setFailed] = useState(false);
  if (logoUrl && !failed) {
    return (
      <img
        src={logoUrl} alt="" width={size} height={size}
        style={{ borderRadius: 5, objectFit: 'contain', flexShrink: 0 }}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span
      title={name || ''}
      style={{
        width: size, height: size, borderRadius: 5, background: 'var(--aivis-bg)',
        border: '1px solid var(--aivis-border)', display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', fontSize: 11, fontWeight: 700,
        color: 'var(--aivis-text-muted)', flexShrink: 0,
      }}
    >
      {(name || '?').slice(0, 1).toUpperCase()}
    </span>
  );
}

function StatCard({ label, value, accent, loading }) {
  return (
    <div style={{ padding: '16px 20px', borderRadius: 12, border: '1px solid var(--aivis-border)', background: 'var(--aivis-panel)' }}>
      <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1, color: accent || 'var(--aivis-text)' }}>
        {loading && (value === '—' || value == null) ? <span style={{ fontSize: 16, color: 'var(--aivis-text-muted)' }}>…</span> : value}
      </div>
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--aivis-text-muted)', marginTop: 6 }}>{label}</div>
    </div>
  );
}

function pct(part, whole) {
  if (!whole) return 0;
  return Math.round((part / whole) * 100);
}

function ProgressBar({ crawled, skipped, total, uncrawled }) {
  // "Done" = total - uncrawled (the UNION of crawled OR skipped). We must NOT
  // use crawled + skipped, because a source can carry both keys and would be
  // double-counted, pushing the bar past 100%.
  const donePct = total ? Math.min(100, ((total - Math.max(0, uncrawled)) / total) * 100) : 0;
  const crawledPct = total ? Math.min(donePct, (crawled / total) * 100) : 0;
  const skippedPct = Math.max(0, donePct - crawledPct);
  return (
    <div style={{ position: 'relative', width: '100%', height: 8, borderRadius: 4, background: 'var(--aivis-bg)', overflow: 'hidden', border: '1px solid var(--aivis-border)' }}>
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${crawledPct}%`, background: 'var(--aivis-positive)' }} />
      <div style={{ position: 'absolute', left: `${crawledPct}%`, top: 0, bottom: 0, width: `${skippedPct}%`, background: 'var(--aivis-border)' }} />
    </div>
  );
}

const COLS = '2fr 1.4fr 90px 90px 90px 90px 1.4fr';

function TableHead() {
  const cells = ['Client', 'Organization', 'Total', 'Crawled', 'Skipped', 'Uncrawled', 'Progress'];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: COLS, gap: 0, padding: '10px 20px', background: 'var(--aivis-bg)', borderBottom: '1px solid var(--aivis-border)' }}>
      {cells.map((c) => (
        <div key={c} style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--aivis-text-muted)' }}>{c}</div>
      ))}
    </div>
  );
}

function CampaignRow({ c, isLast, limit }) {
  return (
    <div
      style={{
        display: 'grid', gridTemplateColumns: COLS, gap: 0, padding: '14px 20px',
        borderBottom: isLast ? 'none' : '1px solid var(--aivis-border)',
        alignItems: 'center',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <ClientLogo logoUrl={c.client_logo_url} domain={c.domain} name={c.name} size={26} />
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--aivis-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
        {c.active ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: 'var(--aivis-info)', background: 'var(--aivis-info-soft)', padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--aivis-info)', animation: 'et-pulse 1.4s ease-in-out infinite' }} />
            Active
          </span>
        ) : null}
        {c.run_gate_blocked ? (
          <span title={`New runs blocked: ${c.uncrawled.toLocaleString()} uncrawled exceed the ${limit} limit`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: 'var(--aivis-caution-ink)', background: 'var(--aivis-caution-soft)', padding: '2px 7px', borderRadius: 999, whiteSpace: 'nowrap' }}>
            Run-gated
          </span>
        ) : null}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }} title={c.organization || ''}>
        <OrgLogo logoUrl={c.organization_logo_url} name={c.organization} size={22} />
      </div>
      <div style={{ fontSize: 13, color: 'var(--aivis-text)', fontVariantNumeric: 'tabular-nums' }}>{c.total.toLocaleString()}</div>
      <div style={{ fontSize: 13, color: 'var(--aivis-positive)', fontVariantNumeric: 'tabular-nums' }}>{c.crawled.toLocaleString()}</div>
      <div style={{ fontSize: 13, color: 'var(--aivis-text-muted)', fontVariantNumeric: 'tabular-nums' }}>{c.skipped.toLocaleString()}</div>
      <div style={{ fontSize: 13, color: c.uncrawled > 0 ? 'var(--aivis-caution-ink)' : 'var(--aivis-text-muted)', fontVariantNumeric: 'tabular-nums', fontWeight: c.uncrawled > 0 ? 600 : 400 }}>{c.uncrawled.toLocaleString()}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <div style={{ flex: 1, minWidth: 60 }}><ProgressBar crawled={c.crawled} skipped={c.skipped} total={c.total} uncrawled={c.uncrawled} /></div>
        <span style={{ fontSize: 11, color: 'var(--aivis-text-muted)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{pct(Math.max(0, c.total - c.uncrawled), c.total)}%</span>
      </div>
    </div>
  );
}

export default function EnrichmentTrackingPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/aivis/system-enrichment/');
      setData(res.data);
      setLastFetched(new Date().toISOString());
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load enrichment tracking.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, 30_000);
    return () => clearInterval(intervalRef.current);
  }, [fetchData]);

  if (!isSystemAdmin(user)) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--aivis-text-muted)', fontSize: 14 }}>
        System admin access required.
      </div>
    );
  }

  const summary = data?.summary ?? {};
  const campaigns = data?.campaigns ?? [];
  const viewerEmail = (user?.email || '').trim().toLowerCase();
  const viewerUsername = (user?.username || '').trim().toLowerCase();
  const showFailedCard = viewerEmail === 'rafay@180marketing.com' || viewerUsername === 'rafay@180marketing.com';

  const sorted = [...campaigns].sort((a, b) => {
    // Active first, then by uncrawled desc (biggest backlog on top), then by name
    if (a.active !== b.active) return a.active ? -1 : 1;
    if (a.uncrawled !== b.uncrawled) return b.uncrawled - a.uncrawled;
    return (a.name || '').localeCompare(b.name || '');
  });

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
        <div>
          <h1 className="aivis-page-title" style={{ marginBottom: 0 }}>Enrichment Tracking</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {lastFetched && (
            <span style={{ fontSize: 12, color: 'var(--aivis-text-muted)' }}>Updated {formatAbsolute(lastFetched)}</span>
          )}
          <button
            type="button"
            onClick={fetchData}
            disabled={loading}
            className="aivis-btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, width: 'auto' }}
          >
            <svg
              width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              style={loading ? { animation: 'et-spin 1s linear infinite' } : {}}
            >
              <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
              <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
              <path d="M16 16h5v5" />
            </svg>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 28 }}>
        <StatCard label="Clients" value={summary.total_campaigns ?? '—'} loading={loading} />
        <StatCard label="Active Crawls" value={summary.active_campaigns ?? '—'} loading={loading} accent={summary.active_campaigns > 0 ? 'var(--aivis-info)' : undefined} />
        <StatCard label="Run-Gated" value={summary.gated_campaigns ?? '—'} loading={loading} accent={summary.gated_campaigns > 0 ? 'var(--aivis-caution-ink)' : undefined} />
        <StatCard label="Total Sources" value={summary.total_sources != null ? summary.total_sources.toLocaleString() : '—'} loading={loading} />
        <StatCard label="Crawled" value={summary.crawled != null ? summary.crawled.toLocaleString() : '—'} loading={loading} accent="var(--aivis-positive)" />
        <StatCard label="Uncrawled" value={summary.uncrawled != null ? summary.uncrawled.toLocaleString() : '—'} loading={loading} accent={summary.uncrawled > 0 ? 'var(--aivis-caution-ink)' : undefined} />
        {showFailedCard ? (
          <StatCard label="Failed" value={summary.failed != null ? summary.failed.toLocaleString() : '—'} loading={loading} accent={summary.failed > 0 ? 'var(--aivis-danger-ink)' : undefined} />
        ) : null}
      </div>

      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 8, background: 'var(--aivis-danger-soft)', color: 'var(--aivis-danger-ink)', fontSize: 13, marginBottom: 20, border: '1px solid #fca5a5' }}>
          {error}
        </div>
      )}

      {loading && !data && (
        <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--aivis-text-muted)', fontSize: 14 }}>
          Loading enrichment tracking…
        </div>
      )}

      {(!loading || data) && (
        sorted.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '64px 0', color: 'var(--aivis-text-muted)', fontSize: 14 }}>
            No clients found.
          </div>
        ) : (
          <div style={{ border: '1px solid var(--aivis-border)', borderRadius: 12, overflow: 'hidden', background: 'var(--aivis-panel)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 20px 7px', background: 'var(--aivis-bg)', borderBottom: '1px solid var(--aivis-border)', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--aivis-text-muted)' }}>Legend:</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--aivis-text-muted)' }}>
                <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: 'var(--aivis-positive)' }} /> Crawled (mention checked)
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--aivis-text-muted)' }}>
                <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: 'var(--aivis-border)' }} /> Skipped
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--aivis-text-muted)' }}>
                <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: 'var(--aivis-caution-soft)', border: '1px solid #fcd34d' }} /> Run-gated (uncrawled &gt; {summary.run_gate_limit ?? 200})
              </span>
              <span style={{ fontSize: 10.5, color: 'var(--aivis-text-muted)', marginLeft: 'auto' }}>
                Crawl order: largest and longest-waiting first
              </span>
            </div>
            <TableHead />
            {sorted.map((c, i) => (
              <CampaignRow key={c.id} c={c} isLast={i === sorted.length - 1} limit={summary.run_gate_limit ?? 200} />
            ))}
          </div>
        )
      )}

      <style>{`
        @keyframes et-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.35; transform: scale(0.7); }
        }
        @keyframes et-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
